import * as React from 'react';
import { cn } from '@workspace/ui/lib/utils';

type Props = {
  current: number;
  max: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  isPostForm?: boolean;
};

export const CircularCharCounter = ({
  current,
  max,
  size = 25,
  strokeWidth = 4,
  className,
  isPostForm = false,
}: Props) => {
  const safeCurrent = Math.min(current, max * 1.2);
  const ratio = safeCurrent / max;
  const radius = size / 2 - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.min(ratio, 1));

  const remaining = Math.max(0, max - current);

  if (isPostForm) {
    const clamped = Math.min(Math.max(current, 0), max);
    const ratio = max === 0 ? 0 : clamped / max;
    const remaining = Math.max(0, max - current);

    const cx = size / 2;
    const cy = size / 2;
    const pad = 1;
    const r = cx - pad;

    const angle = ratio * 360;

    const polarToCartesian = (aDeg: number) => {
      const a = ((aDeg - 90) * Math.PI) / 180;
      return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
    };

    const start = polarToCartesian(0);
    const end = polarToCartesian(angle);
    const largeArc = angle > 180 ? 1 : 0;

    const piePath =
      ratio <= 0
        ? ''
        : ratio >= 1
          ? `M ${cx} ${cy} m -${r} 0 a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 -${r * 2} 0`
          : `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;

    return (
      <div className="flex items-center gap-1">
        <span className="text-sm md:w-7">{remaining}</span>

        <svg width={size} height={size}>
          <circle cx={cx} cy={cy} r={r} fill="#ffffff" />
          {piePath && <path d={piePath} fill="#00C2FF" />}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={1}
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center select-none',
        className
      )}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#00C2FF"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>

      <div className="absolute flex flex-col items-center leading-none text-[8px] font-bold text-gray-800">
        <span>{remaining}</span>
      </div>
    </div>
  );
};
